import React, { useState, useRef, useEffect } from 'react';
import { FiInstagram, FiTwitter, FiFacebook, FiYoutube, FiArrowUp, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';

// SVG payment icons
const VisaIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="24" viewBox="0 0 40 24" fill="none" className="h-6">
    <rect width="40" height="24" rx="2" fill="#111111"/>
    <path d="M15.5 11L13.5 17H11.5L13.5 11H15.5Z" fill="white"/>
    <path d="M20.5 11L18.8 15.2L18.6 14.3L18.6 14.3L18 11.5C18 11.5 18 11 17.3 11H14.6L14.5 11.1C14.5 11.1 15.3 11.3 16.2 11.8L17.8 17H19.9L22.5 11H20.5Z" fill="white"/>
    <path d="M26 17H28L29.5 11H27.5L26 17Z" fill="white"/>
    <path d="M24.8 11L22.6 15.2L22.2 13.5L22.2 13.5L21.5 11.5C21.5 11.5 21.5 11 20.8 11H18.1L18 11.1C18 11.1 18.8 11.3 19.7 11.8L20.6 17H22.7L25.8 11H24.8Z" fill="white"/>
  </svg>
);

const MastercardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="24" viewBox="0 0 40 24" fill="none" className="h-6">
    <rect width="40" height="24" rx="2" fill="#111111"/>
    <path d="M24 15C24 17.2 22.2 19 20 19C17.8 19 16 17.2 16 15C16 12.8 17.8 11 20 11C22.2 11 24 12.8 24 15Z" fill="#EB001B" fillOpacity="0.5"/>
    <path d="M24 15C24 17.2 22.2 19 20 19C18.8 19 17.7 18.5 17 17.7C17.7 16.9 18.8 16.5 20 16.5C21.2 16.5 22.3 17 23 17.7C23.7 16.9 24 16 24 15Z" fill="white"/>
    <path d="M16 15C16 12.8 17.8 11 20 11C21.2 11 22.3 11.5 23 12.3C22.3 13.1 21.2 13.5 20 13.5C18.8 13.5 17.7 13 17 12.3C16.3 13.1 16 14 16 15Z" fill="white"/>
  </svg>
);

const AmexIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="24" viewBox="0 0 40 24" fill="none" className="h-6">
    <rect width="40" height="24" rx="2" fill="#111111"/>
    <path d="M15 14.5H25V17H15V14.5Z" fill="#006FCF" fillOpacity="0.5"/>
    <path d="M15 11H25V13.5H15V11Z" fill="#006FCF" fillOpacity="0.5"/>
    <path d="M11 11H14V17H11V11Z" fill="white"/>
    <path d="M26 11H29V17H26V11Z" fill="white"/>
    <path d="M15 17L16.5 15.5L18 17H15Z" fill="white"/>
    <path d="M15 11L16.5 12.5L18 11H15Z" fill="white"/>
    <path d="M22 11L23.5 12.5L25 11H22Z" fill="white"/>
    <path d="M22 17L23.5 15.5L25 17H22Z" fill="white"/>
  </svg>
);

const PaypalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="24" viewBox="0 0 40 24" fill="none" className="h-6">
    <rect width="40" height="24" rx="2" fill="#111111"/>
    <path d="M14 12.5C14 11.7 14.7 11 15.5 11H18C19.4 11 20.5 12.1 20.5 13.5C20.5 14.9 19.4 16 18 16H16.5L16 17H14.5L16 12.5C16 12.5 15 12.5 14 12.5Z" fill="#253B80" fillOpacity="0.5"/>
    <path d="M17 12.5C17 11.7 17.7 11 18.5 11H21C22.4 11 23.5 12.1 23.5 13.5C23.5 14.9 22.4 16 21 16H19.5L19 17H17.5L19 12.5C19 12.5 18 12.5 17 12.5Z" fill="#179BD7" fillOpacity="0.5"/>
    <path d="M15 12.5C15 12.2 15.2 12 15.5 12H18C18.8 12 19.5 12.7 19.5 13.5C19.5 14.3 18.8 15 18 15H16.5L16 16H15.5L16.5 12.5C16.5 12.5 15.4 12.5 15 12.5Z" fill="white"/>
    <path d="M18 12.5C18 12.2 18.2 12 18.5 12H21C21.8 12 22.5 12.7 22.5 13.5C22.5 14.3 21.8 15 21 15H19.5L19 16H18.5L19.5 12.5C19.5 12.5 18.4 12.5 18 12.5Z" fill="white"/>
  </svg>
);

// The main component
const Footer: React.FC = () => {
  // State for footer visibility
  const footerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Observe footer for entrance animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, []);
  
  return (
    <footer ref={footerRef} className="bg-background-dark relative">
      {/* Decorative Wave */}
      <div className="absolute top-0 left-0 right-0 transform -translate-y-99% overflow-hidden h-16 w-full pointer-events-none">
        <div className="absolute bottom-0 w-full">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
            <path
              fill="#0a0a0a"
              fillOpacity="1"
              d="M0,192L60,186.7C120,181,240,171,360,165.3C480,160,600,160,720,170.7C840,181,960,203,1080,192C1200,181,1320,139,1380,117.3L1440,96L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>
      
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl animate-float"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
      </div>
      
      {/* Footer top border glow */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 relative z-10">
        <div 
          className={`grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {/* Brand */}
          <div className="col-span-1 md:col-span-4">
            <div className="relative mb-6">
              <h2 className="text-3xl font-bold tracking-tight text-primary mb-4">GRAILED804</h2>
              <div className="w-20 h-1 bg-accent mb-6"></div>
            </div>
            
            <p className="text-secondary mb-8 text-base leading-relaxed">
              Premium sneaker marketplace featuring rare grails, hard-to-find sneakers and collaborations.
            </p>
            
            <div className="grid grid-cols-2 gap-y-4 mb-8">
              <div className="flex items-center">
                <FiMapPin className="text-accent mr-3" />
                <span className="text-secondary text-sm">Richmond, VA</span>
              </div>
              <div className="flex items-center">
                <FiMail className="text-accent mr-3" />
                <span className="text-secondary text-sm">contact-us@grailed804.com</span>
              </div>
            </div>
            
            <div className="flex space-x-4 mb-10">
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-background-light flex items-center justify-center hover:bg-accent/10 transition-all duration-300 hover-glow group"
              >
                <FiInstagram className="h-5 w-5 text-accent group-hover:scale-110 transition-transform" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-background-light flex items-center justify-center hover:bg-accent/10 transition-all duration-300 hover-glow group"
              >
                <FiTwitter className="h-5 w-5 text-accent group-hover:scale-110 transition-transform" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-background-light flex items-center justify-center hover:bg-accent/10 transition-all duration-300 hover-glow group"
              >
                <FiFacebook className="h-5 w-5 text-accent group-hover:scale-110 transition-transform" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-background-light flex items-center justify-center hover:bg-accent/10 transition-all duration-300 hover-glow group"
              >
                <FiYoutube className="h-5 w-5 text-accent group-hover:scale-110 transition-transform" />
              </a>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <VisaIcon />
              <MastercardIcon />
              <AmexIcon />
              <PaypalIcon />
            </div>
          </div>

          {/* Navigation columns */}
          <div className="col-span-1 md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
              {/* Shop */}
              <div className="stagger-children">
                <h3 className="text-lg font-semibold text-white mb-6 relative inline-block">
                  Shop
                  <div className="absolute -bottom-2 left-0 w-10 h-0.5 bg-accent"></div>
                </h3>
                <ul className="space-y-4 text-base">
                  <li>
                    <a href="/#featured" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Shop Heat</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="/#categories" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Shop By Category</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="/inventory" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Shop All Inventory</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                </ul>
              </div>
              
              {/* Support */}
              <div className="stagger-children">
                <h3 className="text-lg font-semibold text-white mb-6 relative inline-block">
                  Support
                  <div className="absolute -bottom-2 left-0 w-10 h-0.5 bg-accent"></div>
                </h3>
                <ul className="space-y-4 text-base">
                  <li>
                    <a href="/#authenticity" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Authenticity</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="/#reviews" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Our Reviews</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="/#contact" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Contact Us</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="/#shipping" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Shipping & Returns</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                </ul>
              </div>
              
              {/* Info */}
              <div className="stagger-children">
                <h3 className="text-lg font-semibold text-white mb-6 relative inline-block">
                  Info
                  <div className="absolute -bottom-2 left-0 w-10 h-0.5 bg-accent"></div>
                </h3>
                <ul className="space-y-4 text-base">
                  <li>
                    <a href="#" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">About Us</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Privacy Policy</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Terms of Service</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-secondary inline-block hover:text-accent transition-colors relative overflow-hidden group">
                      <span className="relative z-10">Authenticity Policy</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full"></span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="pt-8 mt-8 border-t border-gray-800/40">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} GRAILED804. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0">
              <ul className="flex flex-wrap space-x-8 text-sm">
                <li className="text-gray-500 hover:text-white transition-colors">
                  <a href="#">Sitemap</a>
                </li>
                <li className="text-gray-500 hover:text-white transition-colors">
                  <a href="#">Cookies</a>
                </li>
                <li className="text-gray-500 hover:text-white transition-colors">
                  <a href="#">Accessibility</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 